import React, { useEffect, useState } from 'react';
import {
  Stack,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
  IconButton,
  Box
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';
import { Exercise } from '../../types';
import { ExerciseAutocomplete } from '../common/ExerciseAutocomplete';
import { History as HistoryIcon } from '@mui/icons-material';
import { WorkoutService } from '../../services/WorkoutService';
import { RecentWorkoutsDialog } from './RecentWorkoutsDialog';

interface WorkoutFormProps {
  exercises: Exercise[];
  exercise: string;
  comment: string;
  setExercise: (value: string) => void;
  setComment: (value: string) => void;
  onCreateExercise?: (name: string) => Promise<Exercise | null>;
  hasError?: boolean;
  errorMessage?: string;
}

interface DetailFieldsProps {
  series: string;
  reps: string;
  weight: string;
  rest_time_seconds?: string;
  rir?: string;
  setSeries: (value: string) => void;
  setReps: (value: string) => void;
  setWeight: (value: string) => void;
  setRestTimeSeconds?: (value: string) => void;
  setRir?: (value: string) => void;
  onClick?: () => void;
  validationErrors?: {
    series?: string;
    reps?: string;
    weight?: string;
    rest_time_seconds?: string;
    rir?: string;
  };
}


const DetailFields: React.FC<DetailFieldsProps> = ({
  series,
  reps,
  weight,
  rest_time_seconds,
  rir,
  setSeries,
  setReps,
  setWeight,
  setRestTimeSeconds,
  setRir,
  onClick,
  validationErrors
}) => {
  const { t } = useLanguage();
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        alignItems="center" 
        justifyContent="space-between"
        onClick={onClick}
      >
        <TextField
          label={t('workout.series')}
          type="number"
          value={series}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSeries(e.target.value)}
          sx={{ flex: 1 }}
          error={!!validationErrors?.series}
          helperText={validationErrors?.series}
        />
        <TextField
          label={t('workout.reps')}
          type="number"
          value={reps}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReps(e.target.value)}
          sx={{ flex: 1 }}
          error={!!validationErrors?.reps}
          helperText={validationErrors?.reps}
        />
        <TextField
          label={t('workout.weight')}
          type="number"
          value={weight}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWeight(e.target.value)}
          sx={{ flex: 1 }}
          error={!!validationErrors?.weight}
          helperText={validationErrors?.weight}
        />
      </Stack>
      
      <Accordion expanded={showAdvanced} onChange={() => setShowAdvanced(!showAdvanced)}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2">{t('workout.advanced')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('workout.restTime')}
              type="number"
              value={rest_time_seconds ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setRestTimeSeconds && setRestTimeSeconds(e.target.value)
              }
              placeholder={t('workout.restTimePlaceholder')}
              helperText={validationErrors?.rest_time_seconds || t('workout.restTimeHelper')}
              sx={{ flex: 1 }}
              error={!!validationErrors?.rest_time_seconds}
            />
            <TextField
              label={t('workout.rir')}
              type="number"
              value={rir ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setRir && setRir(e.target.value)
              }
              placeholder="0-10"
              helperText={validationErrors?.rir || t('workout.rirHelper')}
              inputProps={{ min: 0, max: 10 }}
              sx={{ flex: 1 }}
              error={!!validationErrors?.rir}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

interface WorkoutFormComponent extends React.FC<WorkoutFormProps> {
  DetailFields: React.FC<DetailFieldsProps>;
}

const WorkoutForm: WorkoutFormComponent = ({
  exercises,
  exercise,
  comment,
  setExercise,
  setComment,
  onCreateExercise,
  hasError,
  errorMessage
}) => {
  const { t } = useLanguage();
  const [recentWorkoutsOpen, setRecentWorkoutsOpen] = useState(false);
  const [recentWorkouts, setRecentWorkouts] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);

  const handleExerciseChange = (selectedExercise: Exercise | null) => {
    if (selectedExercise) {
      setExercise(selectedExercise.id.toString());
      setSelectedExercise(selectedExercise);
    } else {
      setExercise('');
      setSelectedExercise(null);
    }
  };

  const handleOpenRecentWorkouts = async () => {
    if (!selectedExercise) {
      return;
    }
    setRecentWorkoutsOpen(true);
    setLoadingRecent(true);
    try {
      const data = await WorkoutService.getExerciseDetails(selectedExercise.id);
      setRecentWorkouts(data.recentWorkouts);
    } catch (error) {
      console.error('Failed to fetch recent workouts', error);
    } finally {
      setLoadingRecent(false);
    }
  };

  const currentExercise = exercises.find((ex) => ex.id.toString() === exercise) || null;
  useEffect(() => {
    if (currentExercise) {
      setSelectedExercise(currentExercise);
    }
  }, [currentExercise]);

  return (
    <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={2} alignItems="flex-end">
        <Box sx={{ flex: 1 }}>
          <ExerciseAutocomplete
            exercises={exercises}
            value={currentExercise}
            onChange={handleExerciseChange}
            onCreateExercise={onCreateExercise}
            label={t('workout.exercise')}
            error={hasError}
            helperText={errorMessage}
            fullWidth
          />
        </Box>
        <Tooltip title={t('recentWorkouts.title', { exerciseName: selectedExercise?.name || '' })}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenRecentWorkouts();
            }}
            disabled={!selectedExercise}
            sx={{ alignSelf: 'center' }}
          >
            <HistoryIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <TextField
        label={t('workout.comment')}
        value={comment}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setComment(e.target.value)}
        fullWidth
        multiline
        rows={2}
        placeholder={t('workout.commentPlaceholder')}
      />
      <RecentWorkoutsDialog
        open={recentWorkoutsOpen}
        onClose={() => setRecentWorkoutsOpen(false)}
        exerciseName={selectedExercise?.name || ''}
        recentWorkouts={recentWorkouts}
        loading={loadingRecent}
      />
    </Stack>
  );
};

// Add DetailFields as a static property of WorkoutForm
WorkoutForm.DetailFields = DetailFields;

export { WorkoutForm, DetailFields }; 